import { FormControlLabel, Stack, Switch } from '@mui/material';
import { LIMITS } from '../constants';
import type { Dictionary } from '../i18n';
import type { EditorState } from '../types';
import { ColorField, SliderField } from './fields';

interface FilterProps {
  t: Dictionary;
  filters: EditorState['filters'];
  backgroundColor: string;
  onChange: (patch: Partial<EditorState['filters']>, tag?: string) => void;
  onBackgroundColor: (value: string) => void;
}

export function FilterControls({
  t,
  filters,
  backgroundColor,
  onChange,
  onBackgroundColor,
}: FilterProps) {
  return (
    <Stack spacing={2}>
      <SliderField
        label={t.brightness}
        unit="%"
        value={filters.brightness}
        min={0}
        max={200}
        onChange={(value) => onChange({ brightness: value }, 'brightness')}
      />
      <SliderField
        label={t.contrast}
        unit="%"
        value={filters.contrast}
        min={0}
        max={200}
        onChange={(value) => onChange({ contrast: value }, 'contrast')}
      />
      <SliderField
        label={t.saturate}
        unit="%"
        value={filters.saturate}
        min={0}
        max={200}
        onChange={(value) => onChange({ saturate: value }, 'saturate')}
      />
      <SliderField
        label={t.blur}
        unit="px"
        value={filters.blur}
        min={LIMITS.blur.min}
        max={LIMITS.blur.max}
        step={0.5}
        onChange={(value) => onChange({ blur: value }, 'blur')}
      />
      <ColorField
        label={t.overlayColor}
        value={filters.overlayColor}
        onChange={(value) => onChange({ overlayColor: value }, 'overlayColor')}
      />
      <SliderField
        label={t.overlay}
        unit="%"
        value={filters.overlayOpacity}
        min={0}
        max={100}
        onChange={(value) => onChange({ overlayOpacity: value }, 'overlayOpacity')}
      />
      <ColorField label={t.backgroundColor} value={backgroundColor} onChange={onBackgroundColor} />
    </Stack>
  );
}

interface FrameProps {
  t: Dictionary;
  frame: EditorState['frame'];
  onChange: (patch: Partial<EditorState['frame']>, tag?: string) => void;
}

export function FrameControls({ t, frame, onChange }: FrameProps) {
  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            checked={frame.enabled}
            onChange={(event) => onChange({ enabled: event.target.checked })}
          />
        }
        label={t.frameEnabled}
      />
      <SliderField
        label={t.thickness}
        unit="px"
        value={frame.width}
        min={LIMITS.frameWidth.min}
        max={LIMITS.frameWidth.max}
        disabled={!frame.enabled}
        onChange={(value) => onChange({ width: value }, 'frameWidth')}
      />
      <ColorField
        label={t.color}
        value={frame.color}
        disabled={!frame.enabled}
        onChange={(value) => onChange({ color: value }, 'frameColor')}
      />
      <SliderField
        label={t.round}
        unit="px"
        value={frame.radius}
        min={LIMITS.frameRadius.min}
        max={LIMITS.frameRadius.max}
        disabled={!frame.enabled}
        onChange={(value) => onChange({ radius: value }, 'frameRadius')}
      />
    </Stack>
  );
}
